import { cmsApi } from '@commerce-webcomponents/sdk';
import { AccountType } from '@nextgen-web-framework/all';
import { Logger } from '@nextgen-web-framework/core';
import {
  CMS_CONTENT_TYPE,
  ProfileImagesDataType,
  ProfileImagesCMSDataProps,
  ProfileImagesResult,
  getProfileImagesCmsDataTransformer,
  updateRegistrationCmsData,
  TermsConditionsCMSDataProps,
  getTermsConditionCmsDataTransformer,
  TermsConditionResult,
  AccountTypeImagesCMSDataProps,
  getAccountTypeImagesCmsDataTransformer,
  AccountTypeImagesResult,
  AccountLifeCycleCMSType,
  AccountLifeCycleCMSDataProps,
  getAccountLifeCycleCmsDataTransformer,
  AccountLifeCycleResult,
  updateAccountLifeCycleCmsData,
  AccountTypeImagesType,
  RegistrationTermsConditionType,
  updateTermsNConditionsCMS,
  updateAccountTypeImagesCMS,
  IRegistrationTermsAndCondition,
  IAccountTypeInfo,
} from '@commerce-profile-webcomponents/sdk';
import { defaultApiErrorHandler, localeCorrection } from './helper';

export const isLocaleDifferent = (locale?: string, cmsLocale?: string): boolean => {
  if (!locale || !cmsLocale) {
    return false;
  }
  const localeLang = locale.split('-')[0].toLowerCase();
  const cmsLang = cmsLocale.split('-')[0].toLowerCase();
  return localeLang !== cmsLang;
};

export const isProfileCMSLoaded = (registrationCmsData?: ProfileImagesDataType) =>
  registrationCmsData && registrationCmsData.title;

export const fetchProfileCMSIfUndefined = async (
  dispatch: Function,
  registrationCmsData?: ProfileImagesDataType,
  locale?: string,
  apiErrorHandler = defaultApiErrorHandler,
) => {
  const logger = Logger.getInstance();
  if (!isProfileCMSLoaded(registrationCmsData) || isLocaleDifferent(locale, registrationCmsData?.locale)) {
    try {
      const cmsResponse = await apiErrorHandler(() =>
        cmsApi.fetchContent({
          uId: CMS_CONTENT_TYPE.PROFILE_IMAGES,
        }),
      );
      const results: ProfileImagesCMSDataProps = getProfileImagesCmsDataTransformer(cmsResponse as ProfileImagesResult);

      dispatch?.(
        updateRegistrationCmsData({
          ...results.data,
        }),
      );
    } catch (error) {
      logger.error('Error while fetching profile images', error);
    }
  }
};

export const fetchTermsNConditionsCMSIfUndefined = async (
  dispatch: Function,
  termsNConditionsCmsData?: { [key: string]: RegistrationTermsConditionType },
  accountType?: AccountType,
  locale?: string,
  apiErrorHandler = defaultApiErrorHandler,
): Promise<void> => {
  const logger = Logger.getInstance();
  const tnCCmsData = accountType ? termsNConditionsCmsData?.[accountType] : undefined;
  if (tnCCmsData && !isLocaleDifferent(locale, tnCCmsData?.locale)) {
    return;
  }
  if (!tnCCmsData || (tnCCmsData?.locale && isLocaleDifferent(locale, tnCCmsData?.locale))) {
    try {
      const cmsResponse = (await apiErrorHandler(() =>
        cmsApi.fetchContent(
          {
            uId: CMS_CONTENT_TYPE.REGISTRATION_TERMS_CONDITIONS,
          },
          {
            // Handling based on user selection - citizen or non-citizen
            headers: {
              'Accept-Language': localeCorrection(locale ?? 'en'),
            },
          },
        ),
      )) as IRegistrationTermsAndCondition;

      const accountTypeTCData = filterTCCMSDataBasedOnAccountType(cmsResponse, accountType);

      const results: TermsConditionsCMSDataProps = getTermsConditionCmsDataTransformer(
        accountTypeTCData as TermsConditionResult,
      );
      results.data.locale = cmsResponse?.publish_details?.locale;

      dispatch(
        updateTermsNConditionsCMS({
          type: accountType,
          data: results.data,
        }),
      );
    } catch (error) {
      logger.error('Error while fetching terms and conditions', error);
    }
  }
};

function filterTCCMSDataBasedOnAccountType(cmsResponse: IRegistrationTermsAndCondition, accountType?: AccountType) {
  const filterData = cmsResponse?.user_info?.find?.((obj) => obj?.account_type?.account_type === accountType);

  return filterData;
}

function filterAccountDetailsCMSDataBasedOnAccountType(cmsResponse: IAccountTypeInfo, accountType?: AccountType) {
  const filterData = cmsResponse?.account_type_details?.find?.(
    (obj) => obj?.user_account?.account_type === accountType,
  );

  return filterData;
}

export const fetchAccountTypeImagesCMSIfUndefined = async (
  dispatch: Function,
  accountTypeImagesCmsData?: { [key: string]: AccountTypeImagesType },
  accountType?: AccountType,
  apiErrorHandler = defaultApiErrorHandler,
): Promise<void> => {
  const logger = Logger.getInstance();
  const imageCmsData = accountType ? accountTypeImagesCmsData?.[accountType] : undefined;
  if (imageCmsData?.landingPageImage) {
    return;
  } else {
    try {
      const cmsResponse = (await apiErrorHandler(() =>
        cmsApi.fetchContent({
          uId: CMS_CONTENT_TYPE.ACCOUNT_TYPE_DETAILS,
        }),
      )) as IAccountTypeInfo;

      const accountData = filterAccountDetailsCMSDataBasedOnAccountType(cmsResponse, accountType);

      const results: AccountTypeImagesCMSDataProps = getAccountTypeImagesCmsDataTransformer(
        accountData as AccountTypeImagesResult,
      );
      results.data.locale = accountData?.publish_details?.locale;

      dispatch(
        updateAccountTypeImagesCMS({
          type: accountType,
          data: results.data,
        }),
      );
    } catch (error) {
      logger.error('Error while fetching account type images', error);
    }
  }
};

export const fetchAccountLifeCycleCMSIfUndefined = async (
  dispatch: Function,
  accountLifeCycleCmsData?: AccountLifeCycleCMSType,
  locale?: string,
  apiErrorHandler = defaultApiErrorHandler,
) => {
  const logger = Logger.getInstance();
  if (!accountLifeCycleCmsData?.title || isLocaleDifferent(locale, accountLifeCycleCmsData?.locale)) {
    try {
      const cmsResponse = await apiErrorHandler(() =>
        cmsApi.fetchContent({
          uId: CMS_CONTENT_TYPE.ACCOUNT_LIFE_CYCLE,
        }),
      );

      const results: AccountLifeCycleCMSDataProps = getAccountLifeCycleCmsDataTransformer(
        cmsResponse as AccountLifeCycleResult,
      );
      dispatch(
        updateAccountLifeCycleCmsData({
          ...results.data,
        }),
      );
    } catch (error) {
      logger.error('Error while fetching account life cycle images', error);
    }
  }
};
